import { useState } from "react";
import GxSmallDialog from "@/app/models/dialogs/GxSmallDialog";
import GxCompactSuccess from "@/app/models/dialogs/SuccessModals/GxCompactSuccess";

import WelcomeStep from "./WelcomeStep";
import StepIndicator from "./StepIndicator";
import StepFooter from "./StepFooter";

import {
  FLOWS,
  initialProfileData,
  initialInviteData,
  initialBeneficiaryData,
  initialSettingsData,
} from "./types";
import ProfileStep from "./ProfileStep";
import InviteStep from "./InviteStep";
import BeneficiaryStep from "./BeneficiaryStep";
import AuthMatrixStep from "./AuthMatrixStep";
import ThirdPartyStep from "./ThirdPartyStep";
import SettingsStep from "./SettingsStep";
import { useNavigate } from "react-router-dom";

const PROFILE_LAST_SUBSTEP = 3;

export default function OnboardingWizard() {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<"welcome" | "wizard" | "skipped">(
    "welcome",
  );
  const [flowIndex, setFlowIndex] = useState(0);
  const [success, setSuccess] = useState<{
    open: boolean;
    primary: string;
    secondary?: string;
  }>({
    open: false,
    primary: "",
  });

  const [profileData, setProfileData] = useState(initialProfileData);
  const [profileSubStep, setProfileSubStep] = useState<0 | 1 | 2 | 3 >(0);

  const [inviteData, setInviteData] = useState(initialInviteData);
  const [inviteSubStep, setInviteSubStep] = useState<0 | 1>(0);

  const [beneficiaryData, setBeneficiaryData] = useState(
    initialBeneficiaryData,
  );
  const [beneficiarySubStep, setBeneficiarySubStep] = useState<0 | 1>(0);

  const [settingsData, setSettingsData] = useState(initialSettingsData);
  const [settingsSubStep, setSettingsSubStep] = useState<0 | 1 | 2 | 3>(0);

  const currentFlow = FLOWS[flowIndex];

  const isLast = flowIndex === FLOWS.length - 1;
  const isFirst = flowIndex === 0;

  const isFinalSubStep = (() => {
    switch (currentFlow.id) {
      case "profile":
        return profileSubStep === PROFILE_LAST_SUBSTEP;
      case "invite":
        return inviteSubStep === 1;
      case "beneficiary":
        return beneficiarySubStep === 1;
      case "settings":
        return settingsSubStep === 3;
      default:
        return true;
    }
  })();
  const isFinalFooterStep = isLast && isFinalSubStep;

  const showSuccess = (primary: string, secondary?: string) => {
    setSuccess({ open: true, primary, secondary });
  };

  const resetSubSteps = () => {
    setProfileSubStep(0);
    setInviteSubStep(0);
    setBeneficiarySubStep(0);
    setSettingsSubStep(0);
  };

  const handleSkip = () => {
    navigate("/dashboard");
  };

  const handleBack = () => {
    if (flowIndex > 0) {
      resetSubSteps();
      setFlowIndex((i) => i - 1);
    }
  };

  const handleStepClick = (index: number) => {
    if (index !== flowIndex) {
      resetSubSteps();
      setFlowIndex(index);
    }
  };

  const handleNext = () => {
    if (currentFlow.id === "profile" && profileSubStep < PROFILE_LAST_SUBSTEP) {
      setProfileSubStep((s) => (s + 1) as 0 | 1 | 2 | 3 );
      return;
    }
    if (currentFlow.id === "invite" && inviteSubStep === 0) {
      setInviteSubStep(1);
      return;
    }
    if (currentFlow.id === "beneficiary" && beneficiarySubStep === 0) {
      setBeneficiarySubStep(1);
      return;
    }
    if (currentFlow.id === "settings" && settingsSubStep < 3) {
      setSettingsSubStep((s) => (s + 1) as 0 | 1 | 2 | 3);
      return;
    }

    switch (currentFlow.id) {
      case "profile":
        showSuccess("Profile updated!", "Your details are saved.");
        break;
      case "invite":
        showSuccess(
          "Invite sent!",
          `${inviteData.firstName || "Teammate"} has been invited.`,
        );
        break;
      case "auth_matrix":
        showSuccess("Auth matrix set up!", "You can fine-tune this later.");
        break;
      case "beneficiary":
        showSuccess(
          "Beneficiary added!",
          beneficiaryData.name
            ? `${beneficiaryData.name} was saved.`
            : "Details saved.",
        );
        break;
      case "third_party":
        showSuccess("Services connected!", "You can manage these anytime.");
        break;
      case "settings":
        showSuccess("Setup complete!", "Your workspace is ready.");
        break;
    }
  };

  const handleSuccessContinue = () => {
    setSuccess({ open: false, primary: "" });
    resetSubSteps();
    if (isLast) navigate("/dashboard");
    else setFlowIndex((i) => i + 1);
  };

  if (phase === "welcome") {
    return (
      <WelcomeStep
        isOpen
        onStart={() => setPhase("wizard")}
        onSkip={() => setPhase("skipped")}
      />
    );
  }

  if (phase === "skipped") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 text-center my-10">
        <p className="gx-p14-sb">
          No problem — you can finish setup later from Settings.
        </p>
      </div>
    );
  }

  return (
    <GxSmallDialog
      isOpen
      onClose={() => setPhase("skipped")}
      width="w-[50vw]"
      maxHeight="max-h-[80vh]"
      title={currentFlow.label}
      subtitle={`Step ${flowIndex + 1} of ${FLOWS.length}`}
    >
      <div className="flex flex-col my-5 ">
        <StepIndicator currentIndex={flowIndex} onStepClick={handleStepClick} />

        {currentFlow.id === "profile" && (
          <ProfileStep
            data={profileData}
            updateField={(field, value) =>
              setProfileData((p) => ({ ...p, [field]: value }))
            }
            subStep={profileSubStep}
            setSubStep={setProfileSubStep}
          />
        )}
        {currentFlow.id === "invite" && (
          <InviteStep
            data={inviteData}
            updateField={(field, value) =>
              setInviteData((p) => ({ ...p, [field]: value }))
            }
            subStep={inviteSubStep}
            setSubStep={setInviteSubStep}
          />
        )}
        {currentFlow.id === "auth_matrix" && <AuthMatrixStep />}
        {currentFlow.id === "beneficiary" && (
          <BeneficiaryStep
            data={beneficiaryData}
            updateField={(field, value) =>
              setBeneficiaryData((p) => ({ ...p, [field]: value }))
            }
            subStep={beneficiarySubStep}
            setSubStep={setBeneficiarySubStep}
          />
        )}
        {currentFlow.id === "third_party" && <ThirdPartyStep />}
        {currentFlow.id === "settings" && (
          <SettingsStep
            data={settingsData}
            updateField={(field, value) =>
              setSettingsData((p) => ({ ...p, [field]: value }))
            }
            subStep={settingsSubStep}
            setSubStep={setSettingsSubStep}
          />
        )}

        <StepFooter
          isLast={isFinalFooterStep}
          isFirst={isFirst}
          onSkip={handleSkip}
          onBack={handleBack}
          onNext={handleNext}
        />
      </div>

      <GxCompactSuccess
        open={success.open}
        close={() => setSuccess({ ...success, open: false })}
        type="true"
        primaryText={success.primary}
        secondaryText={success.secondary}
        buttonLabel={isLast ? "Finish" : "Continue"}
        onButtonClick={handleSuccessContinue}
      />
    </GxSmallDialog>
  );
}
